<script setup lang="ts">
  /**
   * Selection card — 03-components/20c-selection-card · OSCloud DS 1.1.34
   */
  import { computed } from 'vue'

  type SelectionValue = string | number | boolean

  const props = withDefaults(
    defineProps<{
      modelValue?: SelectionValue
      /** 'radio' picks one of a named group; 'checkbox' toggles on its own. */
      type?: 'radio' | 'checkbox'
      /** The value this card contributes when type is 'radio'. */
      value?: SelectionValue
      /** Shared input name for a radio group. */
      name?: string
      label?: string
      /** Optional sub-line under the title. */
      sub?: string
      disabled?: boolean
    }>(),
    {
      modelValue: '',
      type: 'radio',
      value: '',
      name: undefined,
      label: '',
      sub: '',
      disabled: false,
    },
  )

  const emit = defineEmits<{ 'update:modelValue': [value: SelectionValue] }>()

  const isChecked = computed(() => (props.type === 'checkbox' ? !!props.modelValue : props.modelValue === props.value))

  const onChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    emit('update:modelValue', props.type === 'checkbox' ? input.checked : props.value)
  }
</script>

<template>
  <!-- The whole row is the click target; state is read off the input, no modifiers. -->
  <label class="selection-card">
    <input
      :type="type"
      :name="name"
      :value="type === 'radio' ? value : undefined"
      :checked="isChecked"
      :disabled="disabled"
      @change="onChange"
    />
    <span class="selection-card__control">
      <span v-if="type === 'radio'" class="radio__dot" />
      <span v-else class="checkbox__box">
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="3"
          stroke-linecap="square"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
    </span>
    <span v-if="$slots.media" class="selection-card__media"><slot name="media" /></span>
    <span class="selection-card__text">
      <span class="selection-card__label">{{ label }}</span>
      <span v-if="sub" class="selection-card__sub">{{ sub }}</span>
    </span>
  </label>
</template>

<style scoped lang="scss">
  .selection-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--bf-sp-2);
    padding: 14px 16px;
    border-radius: var(--bf-r-lg);
    border: 1px solid var(--bf-line);
    background: var(--bf-white);
    cursor: pointer;
    user-select: none;
    transition:
      background var(--bf-dur-fast) var(--bf-ease),
      border-color var(--bf-dur-fast) var(--bf-ease);

    input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }

    &:hover {
      background: var(--bf-bg-soft);
    }

    &:has(input:checked) {
      background: var(--bf-warning-soft);
      border-color: var(--bf-orange-dark);
      box-shadow: inset 0 0 0 1px var(--bf-orange-dark);
    }

    &:has(input:focus-visible) {
      box-shadow: var(--bf-form-focus-ring);
    }

    &:has(input:disabled) {
      opacity: var(--bf-form-disabled-opacity);
      cursor: not-allowed;
    }

    &__control {
      flex-shrink: 0;
      display: flex;
    }

    &__media {
      flex-shrink: 0;
      width: 46px;
      height: 46px;
      border-radius: var(--bf-r-md);
      background: var(--bf-bg-muted);
      display: flex;
      align-items: center;
      justify-content: center;

      img,
      svg {
        height: 20px;
        width: auto;
      }
    }

    &__text {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    /* 16px since DS 1.1.35: the card's title reads at body size, its sub-line one step down. */
    &__label {
      font-size: var(--bf-fs-base);
      font-weight: var(--bf-fw-semibold);
      color: var(--bf-fg-1);
      overflow-wrap: anywhere;
    }

    &__sub {
      font-size: var(--bf-fs-xs);
      color: var(--bf-fg-2);
    }
  }

  /* The dot and the box are copied from Radio (20a) and Checkbox (20) and never restyled. */
  .radio__dot {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border: 1.5px solid var(--bf-dusky-2);
    border-radius: 50%;
    background: var(--bf-form-control-surface);
    transition:
      border-color var(--bf-dur-fast) var(--bf-ease),
      border-width var(--bf-dur-fast) var(--bf-ease),
      box-shadow var(--bf-dur-fast) var(--bf-ease);
  }

  .selection-card:has(input:checked) .radio__dot {
    border-width: 5px;
    border-color: var(--bf-orange-dark);
  }

  .checkbox__box {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border: 1.5px solid var(--bf-dusky-2);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--bf-form-control-surface);
    transition: all var(--bf-dur-fast) var(--bf-ease);
    flex-shrink: 0;

    svg {
      opacity: 0;
      color: var(--bf-white);
      transition: opacity var(--bf-dur-fast) var(--bf-ease);
    }
  }

  .selection-card:has(input:checked) .checkbox__box {
    background: var(--bf-orange-dark);
    border-color: var(--bf-orange-dark);

    svg {
      opacity: 1;
    }
  }
</style>
